/* PetalPal order tracking — storefront logic (v1)
 * ================================================
 * Pairs with sections/pp-track.liquid + assets/pp-track.css.
 *
 * Talks to Pippy's order-status endpoint through the Shopify App Proxy
 * (/apps/track/order-status), so it is same-origin on every market domain and
 * needs no CORS.
 *
 * ── THE ONE RULE ──────────────────────────────────────────────────────────
 * Everything the server returns is written to the DOM with textContent, never
 * innerHTML. The 2026-07-22 Qikify incident on this store was an HTML-
 * injection skimmer; the 2026-07-27 ParcelPanel finding was a PII leak. This
 * file is deliberately boring about both. If you find yourself reaching for
 * innerHTML or building markup from a string, stop.
 *
 * The server payload contains no address, name, email, phone or price by
 * design (see _ost_public_payload in server.py). Don't add fields here
 * expecting them to arrive.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-pp-track]');
  if (!root) return;

  var cfgEl = root.querySelector('[data-pp-track-config]');
  var cfg = {};
  try {
    cfg = JSON.parse(cfgEl.textContent) || {};
  } catch (e) {
    return; // Inert config unreadable — leave the no-JS markup as-is.
  }

  var formPanel = root.querySelector('[data-pp-track-form-panel]');
  var resultPanel = root.querySelector('[data-pp-track-result-panel]');
  var backBtn = root.querySelector('[data-pp-track-back]');
  var introEl = root.querySelector('[data-pp-track-intro]');
  var form = root.querySelector('[data-pp-track-form]');
  var orderInput = root.querySelector('#pp-track-order');
  var contactInput = root.querySelector('#pp-track-contact');
  var submitBtn = root.querySelector('[data-pp-track-submit]');
  var messageEl = root.querySelector('[data-pp-track-message]');
  var resultEl = root.querySelector('[data-pp-track-result]');
  if (!form || !orderInput || !contactInput || !resultEl) return;

  var endpoint = cfg.endpoint || '/apps/track/order-status';

  // Shopify's `| t` filter HTML-escapes its output, so an apostrophe arrives
  // as "&#39;". Everything here is written with textContent (never innerHTML),
  // so that entity would render literally — "What&#39;s in this order".
  //
  // Decoded with an explicit table + numeric pass rather than the usual
  // `innerHTML` on a detached element. That trick works, but this file's one
  // rule is that no string from outside ever touches an HTML parser, and the
  // 2026-07-22 Qikify skimmer on this store is why. A lookup table can't
  // execute anything.
  var ENTITIES = {
    '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"',
    '&#39;': "'", '&#x27;': "'", '&apos;': "'", '&nbsp;': ' '
  };

  function decodeEntities(str) {
    if (typeof str !== 'string' || str.indexOf('&') === -1) return str;
    return str
      .replace(/&(amp|lt|gt|quot|#39|#x27|apos|nbsp);/g, function (m) {
        return ENTITIES[m] || m;
      })
      .replace(/&#(\d+);/g, function (m, code) {
        var n = parseInt(code, 10);
        return (n > 0 && n < 1114112) ? String.fromCodePoint(n) : m;
      });
  }

  // Decode once, at the edge, so nothing downstream has to remember to.
  function decodeDeep(v) {
    if (typeof v === 'string') return decodeEntities(v);
    if (Array.isArray(v)) return v.map(decodeDeep);
    if (v && typeof v === 'object') {
      var out = {};
      for (var k in v) { if (Object.prototype.hasOwnProperty.call(v, k)) out[k] = decodeDeep(v[k]); }
      return out;
    }
    return v;
  }

  cfg = decodeDeep(cfg);
  var S = cfg.strings || {};

  // Copy comes from section settings (translated per market by Shopify), with
  // the server's English as the fallback. Never hardcode a user-facing string
  // in this file — it would be English in every market forever.
  function t(key, fallback) {
    var v = S[key];
    return (typeof v === 'string' && v) ? v : fallback;
  }

  // A milestone's label + explanation, translated. Falls back to the coarse
  // fulfilment state when there's no checkpoint feed for this order.
  function eventCopy(key) {
    var e = (cfg.events || {})[key];
    if (!e || !e.label) return null;
    return { label: e.label, detail: e.detail || '' };
  }

  function stateCopy(state, data) {
    var st = (cfg.states || {})[state] || {};
    return {
      title: (typeof st.title === 'string' && st.title) ? st.title : data.status_label,
      body: (typeof st.body === 'string' && st.body) ? st.body : data.status_detail
    };
  }

  /* ---------- tiny DOM helpers (textContent only) ---------- */

  function el(tag, className, text) {
    var n = document.createElement(tag);
    if (className) n.className = className;
    if (text !== undefined && text !== null && text !== '') n.textContent = String(text);
    return n;
  }

  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  function setMessage(text, isError) {
    messageEl.textContent = text || '';
    messageEl.classList.toggle('pp-track__note--error', !!isError);
  }

  /* ---------- dates ---------- */

  // "August 3 – 10" (en-US) / "3–10 augustus" (nl-NL). Uses the shopper's
  // market locale so a .nl customer reads a Dutch month name.
  //
  // Intl.DateTimeFormat.formatRange does the whole job and is the ONLY correct
  // way to do this. Hand-concatenating a short start with a long end produced
  // "3–August 10" in en-US, because en-US renders {day, month} as "August 10",
  // not "10 August" — the day/month order is locale-dependent and cannot be
  // assumed. formatRange also picks the right dash and collapses the shared
  // month by itself.
  function formatRange(fromIso, toIso) {
    var locale = cfg.locale || document.documentElement.lang || 'en';
    var a = new Date(fromIso + 'T00:00:00');
    var b = new Date(toIso + 'T00:00:00');
    if (isNaN(a) || isNaN(b)) return '';
    var opts = { day: 'numeric', month: 'long' };
    try {
      var fmt = new Intl.DateTimeFormat(locale, opts);
      if (typeof fmt.formatRange === 'function') {
        return fmt.formatRange(a, b);
      }
      return fmt.format(a) + ' – ' + fmt.format(b);
    } catch (e) {
      return fromIso + ' – ' + toIso;
    }
  }

  function formatDate(iso) {
    if (!iso) return '';
    var locale = cfg.locale || document.documentElement.lang || 'en';
    var d = new Date(iso + 'T00:00:00');
    if (isNaN(d)) return iso;
    try {
      return d.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
    } catch (e) {
      return iso;
    }
  }

  /* ---------- rendering ---------- */

  // Progress is derived from `state`, not from carrier scans. Deliberate:
  // ParcelPanel showed raw carrier jargon ("Info Received", "Expired",
  // "Failed Attempt"), and a customer staring at "Info Received" for five days
  // is exactly who writes in. A state plus a date they can plan around is not.
  var STEPS = [
    { key: 'ordered',   label: 'Ordered' },
    { key: 'preparing', label: 'Being prepared' },
    { key: 'shipped',   label: 'On its way' },
    { key: 'delivered', label: 'Delivered' }
  ];

  function stepIndexFor(state) {
    if (state === 'delivered') return 3;
    // 'late' is still in transit — the parcel hasn't stalled at an earlier
    // stage, it's overdue. Showing it anywhere but "On its way" would misread
    // the situation to someone already anxious.
    if (state === 'shipped' || state === 'partial' || state === 'late') return 2;
    if (state === 'cancelled') return 0;
    return 1; // processing
  }

  function renderProgress(state) {
    var reached = stepIndexFor(state);
    var wrap = el('ol', 'pp-track__steps');
    STEPS.forEach(function (step, i) {
      var li = el('li', 'pp-track__step');
      if (i <= reached) li.classList.add('is-done');
      if (i === reached) li.classList.add('is-current');
      li.appendChild(el('span', 'pp-track__step-dot'));
      li.appendChild(el('span', 'pp-track__step-label', step.label));
      if (i === reached) li.setAttribute('aria-current', 'step');
      wrap.appendChild(li);
    });
    return wrap;
  }

  function renderItems(items) {
    var wrap = el('ul', 'pp-track__items');
    items.forEach(function (item) {
      var li = el('li', 'pp-track__item');
      if (item.image) {
        var img = document.createElement('img');
        img.className = 'pp-track__item-img';
        img.src = item.image;
        img.alt = '';                          // decorative; title follows
        img.loading = 'lazy';
        li.appendChild(img);
      }
      var meta = el('div', 'pp-track__item-meta');
      meta.appendChild(el('span', 'pp-track__item-title', item.title));
      if (item.quantity > 1) {
        meta.appendChild(el('span', 'pp-track__item-qty', '× ' + item.quantity));
      }
      li.appendChild(meta);
      wrap.appendChild(li);
    });
    return wrap;
  }

  function renderTimeline(events) {
    var wrap = el('ol', 'pp-track__journey');
    events.forEach(function (e, i) {
      var li = el('li', 'pp-track__event');
      if (i === 0) li.classList.add('pp-track__event--latest');
      var ec = eventCopy(e.key);
      li.appendChild(el('span', 'pp-track__event-label',
                        (ec && ec.label) || e.key));
      if (e.at) li.appendChild(el('span', 'pp-track__event-date', formatDate(e.at)));
      wrap.appendChild(li);
    });
    return wrap;
  }

  function card(titleText) {
    var c = el('div', 'pp-track__card');
    if (titleText) c.appendChild(el('h2', 'pp-track__card-title', titleText));
    return c;
  }

  // Overview = the answer. Journey = the receipts. A toggle keeps the full
  // scan history one tap away without pushing the summary below the fold.
  function buildToggle(overviewEl, journeyEl) {
    var wrap = el('div', 'pp-track__toggle');
    wrap.setAttribute('role', 'tablist');
    var defs = [
      { label: t('tab_overview', 'Overview'), view: overviewEl },
      { label: t('tab_journey', 'Journey'), view: journeyEl }
    ];
    var btns = [];
    defs.forEach(function (d, i) {
      var b = el('button', 'pp-track__toggle-btn', d.label);
      b.type = 'button';
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      b.addEventListener('click', function () {
        defs.forEach(function (dd, j) {
          var on = i === j;
          btns[j].setAttribute('aria-selected', on ? 'true' : 'false');
          dd.view.hidden = !on;
        });
      });
      btns.push(b);
      wrap.appendChild(b);
    });
    return wrap;
  }

  function render(data) {
    clear(resultEl);

    var layout = el('div', 'pp-track__layout');
    var main = el('div', 'pp-track__main');
    var rail = el('div', 'pp-track__rail');

    /* ---- tracking card ---- */
    var track = el('div', 'pp-track__card');
    var copy = stateCopy(data.state, data);
    var isLate = data.state === 'late';

    var overview = el('div', 'pp-track__view');
    var journey = el('div', 'pp-track__view');
    journey.hidden = true;

    // TOP HEADER: order line, ship date and arrival stacked on the left, the
    // Overview/Journey toggle on the right. All lines share one type treatment
    // so they read as a block of facts rather than a headline plus supporting
    // text. The progress bar then spans full-width beneath, which is what
    // visually closes the header off from the status below it.
    var head = el('div', 'pp-track__card-head');
    var meta = el('div', 'pp-track__meta');

    function metaLine(label, value) {
      var row = el('p', 'pp-track__metaline');
      if (label) row.appendChild(el('span', 'pp-track__meta-label', label + ' '));
      row.appendChild(el('strong', 'pp-track__meta-value', value));
      return row;
    }

    // "Order #52899 · Placed 27 July" — the old Details card carried a single
    // fact, which never justified a card of its own.
    var orderLine = el('p', 'pp-track__metaline');
    orderLine.appendChild(el('strong', 'pp-track__meta-value',
                             t('order_prefix', 'Order') + ' ' + data.order.name));
    if (data.order.placed_at) {
      orderLine.appendChild(el('span', 'pp-track__meta-label',
                               ' · ' + t('placed_label', 'Placed') + ' ' +
                               formatDate(data.order.placed_at)));
    }
    meta.appendChild(orderLine);

    if (data.shipped_at) {
      meta.appendChild(metaLine(t('shipped_prefix', 'Shipped'),
                                formatDate(data.shipped_at)));
    }
    if (data.eta) {
      meta.appendChild(metaLine(t('eta_label', 'Arriving'),
                                formatRange(data.eta.from, data.eta.to)));
    }
    // Carrier and tracking number live here now instead of a separate card.
    if (data.tracking && data.tracking.length) {
      data.tracking.forEach(function (tr) {
        if (tr.company) meta.appendChild(metaLine(t('carrier_label', 'Delivered by'), tr.company));
        if (tr.number) meta.appendChild(metaLine(t('tracking_label', 'Tracking number'), tr.number));
      });
    }

    head.appendChild(meta);
    if (data.timeline && data.timeline.length) {
      head.appendChild(buildToggle(overview, journey));
    }
    track.appendChild(head);

    // Full-width across the card — the visual break between header and status.
    track.appendChild(renderProgress(data.state));

    // The eyebrow IS the recency — "Updated today:" reads as a dateline over
    // the milestone beneath it. A static "Latest" plus a separate "Updated
    // today" line said the same thing twice, in two places.
    var eyebrow = t('latest', 'Latest');
    if (typeof data.days_since_update === 'number') {
      var days = data.days_since_update;
      eyebrow = (days === 0 ? t('updated_today', 'Updated today')
               : days === 1 ? t('updated_yesterday', 'Updated yesterday')
               : t('updated_days', 'Updated {n} days ago').replace('{n}', days)) + ':';
    }
    // Delivery day is the single highest-anxiety moment of the whole journey.
    // It gets its own banner rather than being one more line in a list.
    if (data.latest_key === 'out_for_delivery' && t('today_banner', '')) {
      var today = el('div', 'pp-track__today');
      today.appendChild(el('p', 'pp-track__today-title', t('today_banner', '')));
      today.appendChild(el('p', 'pp-track__today-body', t('today_body', '')));
      overview.appendChild(today);
    }

    var eb = el('p', 'pp-track__eyebrow', eyebrow);
    if (data.update_stale) eb.classList.add('pp-track__eyebrow--stale');
    overview.appendChild(eb);

    // LATEST leads with what actually last happened, not the coarse
    // fulfilment state. "On its way" is the same sentence for two weeks and
    // tells a waiting customer nothing; "Handed to the delivery service" plus
    // an explanation of what that means is the whole point of this tab.
    // The state sentence stays underneath as context, and carries the tab on
    // its own when there's no checkpoint feed for the order.
    var ev = data.latest_key ? eventCopy(data.latest_key) : null;
    var headline = (ev && ev.label) || copy.title;

    var row = el('div', 'pp-track__status-row');
    var icon = el('span', 'pp-track__status-icon');
    if (isLate) icon.classList.add('pp-track__status-icon--late');
    row.appendChild(icon);
    var st = el('h2', 'pp-track__status', headline);
    if (isLate) st.classList.add('pp-track__status--late');
    row.appendChild(st);
    overview.appendChild(row);

    // One explanation, not two. The milestone detail already says where the
    // parcel is; repeating the coarse fulfilment sentence underneath it just
    // made the reader parse the same fact twice.
    overview.appendChild(el('p', 'pp-track__detail',
                            (ev && ev.detail) ? ev.detail : copy.body));

    if (data.timeline && data.timeline.length) {
      journey.appendChild(renderTimeline(data.timeline));
    }

    // Both views must be attached to the card. Leaving this out builds them
    // correctly and inserts neither, which renders a card with a progress bar
    // and nothing else — and a toggle that appears to work while switching two
    // detached nodes.
    track.appendChild(overview);
    track.appendChild(journey);

    // The human line. Reassurance belongs beside the status, not buried in an
    // FAQ the anxious reader never opens. A stale tracking gap gets its own
    // wording — the generic "updates every few days" reads as evasive once
    // it's actually been quiet for a week.
    var noteBody = isLate ? t('note_late', '')
                 : (data.update_stale ? t('quiet_note', '') : t('note_default', ''));
    if (noteBody) {
      var noteEl = el('div', 'pp-track__deliverynote');
      noteEl.appendChild(el('p', 'pp-track__deliverynote-title',
                            t('note_title', 'Good to know')));
      noteEl.appendChild(el('p', null, noteBody));
      track.appendChild(noteEl);
    }
    // The "it says delivered but I don't have it" case — a named CS macro that
    // had no answer on this page. Shown only when it applies, with the actual
    // next step rather than a link into a help centre.
    if (data.state === 'delivered') {
      var miss = el('div', 'pp-track__missing');
      miss.appendChild(el('p', 'pp-track__missing-title',
                          t('missing_title', 'Not there when you looked?')));
      miss.appendChild(el('p', 'pp-track__missing-body', t('missing_body', '')));
      if (cfg.help_url) {
        var cta = el('a', 'pp-track__missing-cta',
                     t('missing_cta', 'Report it missing'));
        cta.href = cfg.help_url;
        miss.appendChild(cta);
      }
      track.appendChild(miss);
    }

    main.appendChild(track);

    /* ---- rail ---- */
    if (data.items && data.items.length) {
      var itemsCard = card(t('items_title', "What's in your order"));
      itemsCard.appendChild(renderItems(data.items));
      rail.appendChild(itemsCard);
    }

    // Only the questions that apply to where this order actually is. Showing
    // all of them in every state means most are noise, and noise is what
    // teaches someone to stop reading the page and open a ticket instead.
    // "It arrived flat — is it damaged?" is a real ticket type for a product
    // that ships flat by design. Explaining it BEFORE the parcel lands costs
    // nothing; explaining it afterwards costs a support conversation.
    var faqs = (cfg.faqs || []).filter(function (f) {
      if (!f || !f.q || !f.a) return false;
      if (!f.states) return true;
      return f.states.split(',').some(function (st) {
        return st.trim() === data.state;
      });
    });
    // "It arrives flat" used to be its own card above this one. It says the
    // same kind of thing the questions below say, and a second card costs a
    // full card's padding and gap in a rail that was already taller than the
    // status column beside it — which pushed the guarantees strip far below
    // the fold on desktop. Folded in as the first question instead: same
    // bubble, same width, still the first thing read.
    if (t('flat_title', '') && data.state !== 'processing') {
      faqs = [{ q: t('flat_title', ''), a: t('flat_body', '') }].concat(faqs);
    }

    if (faqs.length) {
      var helpCard = card(t('faq_title', 'Common questions'));
      var list = el('div', 'pp-track__faq');
      faqs.forEach(function (f) {
        var d = document.createElement('details');
        d.className = 'pp-track__faq-item';
        var sum = document.createElement('summary');
        sum.className = 'pp-track__faq-q';
        sum.textContent = f.q;
        d.appendChild(sum);
        d.appendChild(el('p', 'pp-track__faq-a', f.a));
        list.appendChild(d);
      });
      helpCard.appendChild(list);
      rail.appendChild(helpCard);
    }

    layout.appendChild(main);
    if (rail.childNodes.length) layout.appendChild(rail);
    resultEl.appendChild(layout);

    var gs = (cfg.guarantees || []).filter(function (g) {
      return typeof g === 'string' && g;
    });
    if (gs.length) {
      var gCard = el('div', 'pp-track__card pp-track__card--guarantees');
      var ul = el('ul', 'pp-track__guarantees');
      gs.forEach(function (g) { ul.appendChild(el('li', 'pp-track__guarantee', g)); });
      gCard.appendChild(ul);
      resultEl.appendChild(gCard);
    }

    resultEl.setAttribute('tabindex', '-1');
  }

  /* ---------- remembering the shopper ----------
   * Three honest ways to skip retyping, in order of preference:
   *   1. a real logged-in Shopify session (rendered by Liquid, server-side)
   *   2. a lookup this browser already completed successfully
   *   3. the form
   * Never by IP — see the note in order-tracking.liquid.
   */

  // Fire-and-forget into the first-party collector that already handles the
  // cart and the thank-you survey. Consent-gated and rate-limited there, so
  // there is nothing to re-implement here.
  function track(eventName, props) {
    if (!cfg.track_url || !cfg.track_key) return;
    try {
      var body = JSON.stringify({
        event_name: eventName,
        event_id: eventName + '-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
        client_id: (document.cookie.match(/pp_cid=([^;]+)/) || [])[1] || 'anon',
        consent: { analytics: true },
        props: props || {}
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(cfg.track_url + '/' + cfg.track_key, body);
      } else {
        fetch(cfg.track_url + '/' + cfg.track_key,
              { method: 'POST', body: body, keepalive: true });
      }
    } catch (e) { /* analytics must never break the page */ }
  }

  var STORE_KEY = 'pp_track_last';

  function remember(orderNumber, contact) {
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify({
        o: orderNumber, c: contact, t: Date.now()
      }));
    } catch (e) { /* private mode / storage disabled — not important */ }
  }

  function recall() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      // 60 days: long enough to cover a slow delivery and a follow-up, short
      // enough that a shared or family computer doesn't keep an old order
      // on screen indefinitely.
      if (!v || !v.o || !v.c || (Date.now() - (v.t || 0)) > 5184000000) {
        forget(); return null;
      }
      return v;
    } catch (e) { return null; }
  }

  function forget() {
    try { window.localStorage.removeItem(STORE_KEY); } catch (e) {}
  }

  function showResultPanel() {
    formPanel.hidden = true;
    resultPanel.hidden = false;
    if (introEl) introEl.hidden = true;
  }

  function showFormPanel() {
    resultPanel.hidden = true;
    formPanel.hidden = false;
    if (introEl) introEl.hidden = false;
    clear(resultEl);
  }

  if (backBtn) {
    backBtn.addEventListener('click', function () {
      forget();
      orderInput.value = '';
      contactInput.value = '';
      setMessage('', false);
      showFormPanel();
      orderInput.focus();
    });
  }

  /* ---------- submit ---------- */

  var busy = false;

  function lookup(orderNumber, contact, opts) {
    if (busy) return;
    opts = opts || {};

    // A signed link supplies proof instead of a contact, so the form's
    // both-fields rule doesn't apply to it.
    if (!orderNumber || (!contact && !opts.token)) {
      setMessage(t('need_both', 'Please enter both your order number and your email or phone.'), true);
      return;
    }

    busy = true;
    submitBtn.disabled = true;
    submitBtn.classList.add('is-busy');
    if (!opts.quiet) setMessage(t('looking', 'Looking up your order…'), false);
    clear(resultEl);

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        order_number: orderNumber,
        contact: contact,
        token: opts.token || ''
      })
    })
      .then(function (res) { return res.json().catch(function () { return {}; }); })
      .then(function (data) {
        if (data && data.ok) {
          setMessage('', false);
          render(data);
          remember(orderNumber, contact);
          showResultPanel();
          // Without this we cannot tell whether the page reduced tickets or
          // just moved them. State + entry route are the two cuts that matter:
          // "how many people checked, at what stage, and did they still write
          // in" is the only honest measure of a WISMO page.
          track('order_lookup', {
            state: data.state || '',
            latest: data.latest_key || '',
            via: opts.token ? 'email_link' : (opts.quiet ? 'remembered' : 'form'),
            stale: !!data.update_stale
          });
        } else {
          // The server returns one identical body for "no such order" and
          // "wrong contact" on purpose — it must not confirm that an order
          // number exists. Show whatever it sent, verbatim.
          // A silent auto-attempt that fails should never accuse the shopper
          // of anything — just quietly leave the form for them to fill in.
          if (opts.quiet) {
            forget();
            showFormPanel();
          } else {
            setMessage(
              (data && data.message) ||
              'We couldn’t find an order with those details.',
              true
            );
          }
        }
      })
      .catch(function () {
        if (opts.quiet) { showFormPanel(); return; }
        setMessage(t('generic_error', 'Something went wrong on our end. Please try again in a moment.'), true);
      })
      .then(function () {
        busy = false;
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-busy');
      });
  }

  form.addEventListener('submit', function (ev) {
    ev.preventDefault();
    lookup((orderInput.value || '').trim(), (contactInput.value || '').trim());
  });

  /* ---------- what happens on load ----------
   * In priority order:
   *   1. `?order=` — prefill the number only, never auto-submit. A link that
   *      revealed an order on its own is the ParcelPanel bug this replaces.
   *   2. A remembered lookup from this browser — re-fetched, never rendered
   *      from cache, so the status is always current.
   *   3. A logged-in Shopify customer — their own session, their own last
   *      order. Emitted by Liquid only inside `{% if customer %}`.
   * If a silent attempt fails we fall back to the form without a word: the
   * shopper never asked for it, so it must not produce an error they have to
   * interpret.
   */
  var linkToken = '';
  try {
    var qs = new URLSearchParams(window.location.search);
    var pre = (qs.get('order') || '').replace(/[^A-Za-z0-9-]/g, '').slice(0, 32);
    linkToken = (qs.get('t') || '').replace(/[^a-f0-9]/gi, '').slice(0, 64);
    if (pre) {
      orderInput.value = pre;
      if (!linkToken) contactInput.focus();
    }
  } catch (e) { /* URLSearchParams unavailable — prefill is optional */ }

  // A signed link from a Shopify notification email opens straight onto the
  // order with nothing to type. `?order=` alone still only PREFILLS — the
  // token is what proves the customer was sent this, and without one the
  // contact field is still required. That distinction is the whole reason the
  // ParcelPanel `?nums=` link was unsafe and this one isn't.
  if (pre && linkToken) {
    lookup(pre, '', { quiet: true, token: linkToken });
  } else {

  var saved = recall();
  if (saved) {
    orderInput.value = saved.o;
    contactInput.value = saved.c;
    lookup(saved.o, saved.c, { quiet: true });
  } else if (cfg.customer_email && cfg.customer_last_order) {
    orderInput.value = String(cfg.customer_last_order).replace(/^#/, '');
    contactInput.value = cfg.customer_email;
    lookup(orderInput.value, contactInput.value, { quiet: true });
  }
  }

})();
